import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const PAY_METHODS = [
  { v: "efectivo", label: "Efectivo" },
  { v: "tarjeta", label: "Tarjeta" },
  { v: "transferencia", label: "Transferencia" },
  { v: "bizum", label: "Bizum" },
  { v: "domiciliacion", label: "Domiciliación" },
  { v: "otro", label: "Otro" },
];

const EMPTY = { account_id: "", date: "", concept: "", amount: "", pay_method: "", category_id: "" };

export function AddExpenseDialog({ open, onOpenChange, accounts, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, date: new Date().toISOString().slice(0, 10) });
      api.get("/categories")
        .then((r) => setCategories(r.data.filter((c) => c.group !== "otros_ingresos")))
        .catch(() => {});
    }
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/banks/transactions", {
        account_id: form.account_id, date: form.date, concept: form.concept.trim(),
        amount: Number(form.amount), pay_method: form.pay_method, category_id: form.category_id,
      });
      toast.success("Gasto registrado y categorizado");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar el gasto");
    } finally {
      setSaving(false);
    }
  };

  const valid = form.account_id && form.date && form.concept.trim()
    && Number(form.amount) > 0 && form.pay_method && form.category_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="expense-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Añadir gasto manual</DialogTitle>
          <DialogDescription>
            Para gastos sin comprobante. Queda registrado en la cuenta y categorizado en el P&L directamente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Cuenta desde la que se pagó</Label>
            <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
              <SelectTrigger data-testid="expense-account"><SelectValue placeholder="Selecciona cuenta..." /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Fecha</Label>
              <Input data-testid="expense-date" type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Importe (€)</Label>
              <Input data-testid="expense-amount" type="number" step="0.01" min="0" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9 font-num" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Concepto</Label>
            <Input data-testid="expense-concept" value={form.concept}
              onChange={(e) => setForm({ ...form, concept: e.target.value })} className="h-9"
              placeholder="Qué se pagó..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Cómo se pagó</Label>
              <Select value={form.pay_method} onValueChange={(v) => setForm({ ...form, pay_method: v })}>
                <SelectTrigger data-testid="expense-method"><SelectValue placeholder="Método..." /></SelectTrigger>
                <SelectContent>
                  {PAY_METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Categoría</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="expense-category"><SelectValue placeholder="Categoría..." /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="expense-cancel" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="expense-save" onClick={save} disabled={!valid || saving}
              className="bg-[#D9381E] hover:bg-[#B91C1C] text-white">
              {saving ? "Guardando..." : "Registrar gasto"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
